import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';

// layout 
import AuthLayout from '../../../layouts/AuthLayout/AuthLayout';

// components
import { NioBrand, NioButton, NioCard } from '../../../components';
import NioField from '../../../components/NioField/NioField';
import NioToaster from '../../../components/NioToaster/NioToaster';
import api, { storageSafeUser } from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';


export default function Login() {

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const methods = useForm();
  const { register, handleSubmit, formState: { errors } } = methods;
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [loginError, setLoginError] = useState(false);


  async function onSubmit(data) {
    setLoading(true);
    setToastMessage(null);
    setLoginError(false);

    try {
      const response = await api.login({
        email: data.email,
        password: data.password
      });

      if (response.success) {
        setToastMessage({ variant: 'success', header: t('auth.pageTitle'), body: t('auth.loginSuccess') });
        // Store user data in localStorage
        try {
          localStorage.setItem('user', JSON.stringify(storageSafeUser(response.data)));
        } catch (storageError) {
          // Fallback: keep only essential identity/session fields.
          const minimal = response?.data
            ? {
              id: response.data.id || response.data._id,
              _id: response.data._id,
              name: response.data.name,
              lastName: response.data.lastName,
              email: response.data.email,
              role: response.data.role,
              verificationStatus: response.data.verificationStatus,
              token: response.data.token,
            }
            : null;
          if (minimal) {
            localStorage.setItem('user', JSON.stringify(minimal));
          }
          // eslint-disable-next-line no-console
          console.warn('localStorage quota reached. Stored minimal user payload instead.', storageError);
        }
        const redirectTo = location.state?.from || '/index-bs-management';
        navigate(redirectTo, { replace: true });
      }
    } catch (error) {
      setLoginError(true);
      setToastMessage({ 
        variant: 'danger', 
        header: t('auth.loginFailed'), 
        body: error.message || t('auth.loginFailedBody'),
      });
    } finally {
      setLoading(false);
    }
  }


  return (
    <AuthLayout title={t('auth.pageTitle')} rootClass="layout-1">
      <NioCard className="nk-form-card rounded-3 card-gutter-md nk-auth-form-card mx-md-9 mx-xl-auto" >
        <NioCard.Body className="p-5">
          <div className="nk-form-card-head text-center pb-5">
            <div className="form-logo mb-3">
              <NioBrand
                logo="logo1"
                variant="dark"
                imageRoot="../images/"
              />
            </div>
            <h3 className="title mb-2">{t('auth.loginTitle')}</h3>
            <p className="text">{t('auth.noAccount')} <Link to="/auth/sign-up" className="btn-link text-primary">{t('auth.signupLink')}</Link>.</p>
          </div>
          {toastMessage && (
            <NioToaster 
              variant={`${toastMessage.variant} text-white`} 
              messages={[{ id: 1, header: toastMessage.header, body: toastMessage.body }]} 
            />
          )}
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Row className="gy-4">
                <Col xs={12}>
                  <NioField htmlFor="email" label={t('auth.email')}>
                    <NioField.Input
                      id="email"
                      type="email"
                      htmlFor="email"
                      placeholder={t('auth.emailPlaceholder')}
                      errors={errors.email || (loginError ? { message: '' } : null)}
                      {...register("email", {
                        required: t('auth.emailRequired'),
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: t('auth.emailInvalid'),
                        },
                      })}
                    />
                  </NioField>
                </Col>
                <Col xs={12}>
                  <NioField htmlFor="password" label={t('auth.password')}>
                    <NioField.Input
                      id="password"
                      type="password"
                      errors={errors.password || (loginError ? { message: '' } : null)}
                      {...register("password", {
                        required: t('auth.passwordRequired'),
                      })}
                      icon="eye-fill after text-primary"
                      placeholder={t('auth.passwordPlaceholder')}
                    />
                  </NioField>
                </Col>
                <Col xs={12}>
                  <div className="d-flex flex-wrap align-items-center  justify-content-between text-center">
                    <NioField.Check id="check1" {...register("check1")}>
                      {t('auth.rememberMe')}
                    </NioField.Check>
                    <a href="/auth/forgot-password" className="d-inline-block fs-16">{t('auth.forgotPassword')}</a>
                  </div>
                </Col>
                <Col xs={12}>
                  <div className="form-group">
                    <NioButton
                      label={loading ? t('auth.loggingIn') : t('auth.loginButton')}
                      className="btn-block btn-primary"
                      type="submit"
                      disabled={loading}
                    />
                  </div>
                </Col>
              </Row>
            </form>
          </FormProvider>
        </NioCard.Body>
      </NioCard>
    </AuthLayout >
  )
}
